import { validate } from 'rut.js';
import { z } from 'zod';
import { ContractSigningKindOptionsMap } from './constants';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1000 * 1000;
const MAX_FILES_ALLOWED = 10;

const signersSchema = z.array(z.object({
  rut: z.string().min(1, 'Este campo es requerido').refine((rut) => validate(rut), {
    message: 'El formato del RUT ingresado no es válido',
  }),
  email: z.string().email('El formato del email ingresado no es válido'),
})).superRefine((signers, ctx) => {
  const seen = new Map<string, number>();
  signers.forEach((signer, i) => {
    const rut = signer.rut?.trim();
    if (!rut) return;
    if (seen.has(rut)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'No es posible repetir el RUT entre los apoderados',
        path: [i, 'rut'],
      });
    } else {
      seen.set(rut, i);
    }
  });
});

const documentsSchema = z.array(z.object({
  raw: z.instanceof(File),
  id: z.string(),
  document_type_id: z.number(),
})).min(
  1,
  'Debes cargar al menos 1 archivo',
).refine((data) => data.reduce((acc, file) => acc + file.raw.size, 0) <= MAX_FILE_SIZE, {
  message: `El tamaño total de los archivos no puede exceder ${MAX_FILE_SIZE_MB} MB`,
}).refine((data) => data.length <= MAX_FILES_ALLOWED, {
  message: `Se ha superado el número máximo de archivos permitidos para subir (${MAX_FILES_ALLOWED}).`,
});

export const contractSigningFormSchema = z.object({
  clientRUT: z.string().min(1, 'Este campo es requerido').refine((rut) => validate(rut), {
    message: 'El formato del RUT ingresado no es válido',
  }),
  clientEmail: z.string().email('El formato del email ingresado no es válido').optional(),
  signers: signersSchema,
  documentsToSign: documentsSchema,
  contractSigningKind: z.nativeEnum(ContractSigningKindOptionsMap),
})
  .refine((data) => !data?.signers?.some((signer) => signer.rut === data.clientRUT), {
    message: 'El RUT del cliente no puede ser el mismo que el de los apoderados',
    path: ['clientRUT'],
  })
  .refine((data) => {
    if (data.signers.length === 0) {
      return !!data.clientEmail;
    }
    return true;
  });

export type ContractSigningForm = z.infer<typeof contractSigningFormSchema>;
